import { useContext, useEffect, useState } from 'react';
import { Stack, Paper, Typography, Button } from "@mui/material";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useNavigate, useParams } from "react-router-dom";
import Context from "../../context/Context";
import { getAllInstruments, getInstrumentDetail } from "../../api/app/instrumentRequests";
import { generateTechnicalAnalysis } from "../../api/app/senseStream";
import {
    getComplementaryHistory,
    getInitialHistoricPricePoints,
    getRangedHistoricPricePoints
} from "../../api/app/historicRequests";
import {
    amountFormatter,
    booleanDataPointFormatter,
    capitalizeEachFirstLetterFormatter,
    currencyAmountFormatter,
    dateFormatter,
    bondNameFormatter
} from "../../utils/formatters";
import { getDateCorrespondingToRange } from "../../utils/chart";
import { historicPlottingRanges } from "../../constants/plottingConstants"
import { CompoundHeader, HistoricChart, HighlightCards, DataCard, SenseTechnicalAnalysisCard } from "./components"
import LoadingWrapper from "../../components/LoadingWrapper";
import SimpleInstrumentGroup from "../../components/SimpleInstrumentGroup";
import ResponsiveLayout from "../../components/layout/ResponsiveLayout";

const TEMP_ANALYSIS = "1. <strong>Summary Verdict:</strong> Hold — the bond is trading at a high price (129.361% of face), but key details (coupon and yield) are missing; don’t buy until you know the yield and why you would pay a premium.\n2. <strong>Key Facts:</strong><br>Issuer: <mark>República de Brasil</mark><br>Coupon: <mark>Not provided</mark><br>Maturity: <mark>6 March 2030</mark><br>Price: <mark>129.361% of face value (USD)</mark><br>Yield to Maturity: <mark>Not provided</mark>3. <strong>Market Context:</strong><br>A price above 100% means the bond is trading at a premium — investors are paying extra, usually because the coupon is higher than current market rates. This behaves like other emerging-market sovereign USD bonds: generally more liquid than small corporates, but traded volume can vary by issue.4. <strong>Risks Explained:</strong><br>- Interest rate risk: If global/US rates rise, the premium price can drop. - Credit risk: Brazil’s economy affects ability to pay; country risk matters. - Inflation risk: High USD inflation reduces the real return. - Currency risk: Low for USD investors because this bond is dollar‑denominated.5. <strong>Investor Suitability:</strong><br>Best for income-oriented investors comfortable with emerging‑market sovereign risk who confirm the coupon/YTM justify the premium. If you can’t get the yield numbers, avoid buying."

const Bond = () => {

    const { id } = useParams()

    const navigate = useNavigate()

    const { showSnackbar } = useContext(Context)

    const [isBondDataLoading, setIsBondDataLoading] = useState(true)
    const [isSenseTechnicalAnalysisLoading, setIsSenseTechnicalAnalysisLoading] = useState(false)

    const [senseTechnicalAnalysisThoughtTime, setSenseTechnicalAnalysisThoughtTime] = useState(0)

    const [senseTechnicalAnalysis, setSenseTechnicalAnalysis] = useState("")

    const [bondDetail, setBondDetail] = useState(null)
    const [relatedBonds, setRelatedBonds] = useState([])
    const [historicPoints, setHistoricPoints] = useState([])
    const [plottingHistoricPoints, setPlottingHistoricPoints] = useState([])
    const [plottingRange, setPlottingRange] = useState("")
    const [plottingType, setPlottingType] = useState("")
    const [complementaryHistory, setComplementaryHistory] = useState(null)
    const [complementaryHistoryDisplay, setComplementaryHistoryDisplay] = useState(null)
    const [bondHighlights, setBondHighlights] = useState([])
    const [generalInformation, setGeneralInformation] = useState([])
    const [masterInformation, setMasterInformation] = useState([])
    const [creditRatingInformation, setCreditRatingInformation] = useState([])
    const [chartRangeToggleOptions, setChartRangeToggleOptions] = useState([])
    const [chartTypeToggleOptions] = useState([
        { label: "Price", value: "price" },
        { label: "Yield", value: "yield" },
    ])

    const clearState = () => {
        setBondDetail(null)
        setComplementaryHistory(null)
        setPlottingHistoricPoints([])
    }

    const handleGenerateTechnicalAnalysis = () => {
        setIsSenseTechnicalAnalysisLoading(true)
        generateTechnicalAnalysis(bondDetail.isin, setSenseTechnicalAnalysis, setIsSenseTechnicalAnalysisLoading, setSenseTechnicalAnalysisThoughtTime).catch(e => {
            showSnackbar(e.message, 'warning');
        })
    };

    const handlePlottingRangeChangeHandler = (event, range) => {
        if (range !== null) {
            setPlottingRange(range);
        }
    }

    const handlePlottingTypeChangeHandler = (event, type) => {
        if (type !== null) {
            setPlottingType(type);
        }
    }

    const plotHistoricPoint = (targetLimitDate) => {
        const rangedPlottingPoints = historicPoints.filter(point => point.sessionDate > targetLimitDate)
        setPlottingHistoricPoints(rangedPlottingPoints)
    }

    const handleOpenBondAnalyzer = () => {
        navigate(`/omnichart/`, { state: { instrumentId: bondDetail.isin } })
    }

    const handleCopyIsin = () => {
        try {
            navigator.clipboard.writeText(bondDetail.isin)
            showSnackbar("ISIN copied into clipboard", 'success');
        } catch (e) {
            showSnackbar("Unable to copy ISIN into clipboard", 'error');
        }
    }

    useEffect(() => {
        if (bondDetail) {

            getInitialHistoricPricePoints(id, new Date(bondDetail.lastSessionDate), 5).then((points) => { setHistoricPoints(points) }).catch(e => {
                showSnackbar(e.message, 'warning');
            })

            getAllInstruments(0, 3, false, bondDetail.issuer, "rate", true).then(instruments => { setRelatedBonds(instruments) }).catch(e => {
                showSnackbar(e.message, 'warning');
            })

            setBondHighlights([
                { label: 'Coupon', value: bondDetail.rate.toFixed(2) + '%' },
                { label: 'Yield To Maturity', value: bondDetail.yield.toFixed(2) + '%' },
                { label: 'Currency', value: bondDetail.currency },
                { label: 'Credit Rating', value: bondDetail.creditRating },
            ])

            setGeneralInformation([
                { label: 'Issuance type', value: bondDetail.issuanceType },
                { label: 'Instrument type', value: bondDetail.instrumentType },
                { label: 'Issuer code', value: bondDetail.issuer },
                { label: 'Interest payment count (per year)', value: bondDetail.peridiocity },
                { label: 'Official', value: booleanDataPointFormatter(bondDetail.official) },
                { label: 'Buy back', value: booleanDataPointFormatter(bondDetail.buyBack) },

            ])

            setMasterInformation([
                { label: 'Series', value: bondDetail.series },
                { label: 'Issuer name', value: capitalizeEachFirstLetterFormatter(bondDetail.issuerName) },
                { label: 'Country', value: bondDetail.country },
                { label: 'Currency', value: bondDetail.currency },
                { label: 'Maturity', value: dateFormatter(bondDetail.maturity, "long") },
                { label: 'Coupon', value: bondDetail.rate.toFixed(2) + '%' },
                { label: 'Yield To Maturity', value: bondDetail.yield.toFixed(2) + '%' },
                { label: 'Approved amount', value: currencyAmountFormatter(bondDetail.currency, bondDetail.approvedAmount) },
                { label: 'Placed amount', value: currencyAmountFormatter(bondDetail.currency, bondDetail.placedAmount) },
                { label: 'First session recorded', value: dateFormatter(bondDetail.firstSessionDate, 'long') },
                { label: 'Last session recorded', value: dateFormatter(bondDetail.lastSessionDate, 'long') },
            ])

            let availableCreditRatings = []

            if (bondDetail.moodysRatingCode) {
                availableCreditRatings = [
                    ...availableCreditRatings,
                    { label: 'Moody\'s rating', value: bondDetail.moodysRatingCode },
                    { label: 'Moody\'s rating last update', value: dateFormatter(bondDetail.moodysLastUpdated, "long") },
                ]
            }

            if (bondDetail.standardPoorsRatingCode) {
                availableCreditRatings = [
                    ...availableCreditRatings,
                    { label: 'Standard & Poor\'s rating', value: bondDetail.standardPoorsRatingCode },
                    { label: 'Standard & Poor\'s rating last update', value: dateFormatter(bondDetail.standardPoorsLastUpdated, "long") },
                ]
            }

            if (bondDetail.fitchRatingCode) {
                availableCreditRatings = [
                    ...availableCreditRatings,
                    { label: 'Fitch rating', value: bondDetail.fitchRatingCode },
                    { label: 'Fitch rating last update', value: dateFormatter(bondDetail.fitchLastUpdated, "long") },
                ]
            }

            setCreditRatingInformation(availableCreditRatings)

            setPlottingRange(getDateCorrespondingToRange(new Date(bondDetail.lastSessionDate), new Date(bondDetail.firstSessionDate), historicPlottingRanges[0]))
            setPlottingType(chartTypeToggleOptions[0].value)
            setChartRangeToggleOptions(historicPlottingRanges.map((range) => {
                return ({
                    label: range,
                    value: getDateCorrespondingToRange(new Date(bondDetail.lastSessionDate), new Date(bondDetail.firstSessionDate), range)
                });
            }))
        }
    }, [bondDetail])

    useEffect(() => {
        if (historicPoints.length) {
            const currentLimitDate = new Date(historicPoints[0].sessionDate)
            const targetLimitDate = new Date(plottingRange)
            if (targetLimitDate < currentLimitDate) {
                getRangedHistoricPricePoints(id, targetLimitDate, currentLimitDate).then((rangedPoints) => {
                    setHistoricPoints(rangedPoints.concat(historicPoints))
                }).catch(e => {
                    showSnackbar(e.message, 'warning');
                })
            } else {
                plotHistoricPoint(new Date(plottingRange))
            }
        }
    }, [plottingRange])

    useEffect(() => {
        plotHistoricPoint(new Date(plottingRange))
    }, [historicPoints])

    useEffect(() => {
        setIsBondDataLoading(true)
        clearState()
        getInstrumentDetail(id).then((detail) => { setBondDetail(detail) }).catch(e => {
            showSnackbar(e.message, 'warning');
        })
        getComplementaryHistory(id).then((history) => { setComplementaryHistory(history) }).catch(e => {
            showSnackbar(e.message, 'warning');
        })
    }, [id])

    useEffect(() => {
        if (complementaryHistory) {
            if (complementaryHistory.lastYearsPriceYieldLimits) {
                setComplementaryHistoryDisplay([
                    { label: "Day trading volume level", value: capitalizeEachFirstLetterFormatter(complementaryHistory.current_activity_level) },
                    { label: "Current trading volume level", value: capitalizeEachFirstLetterFormatter(complementaryHistory.current_activity_level) },
                    { label: "Last 52 week price range", value: `${amountFormatter(complementaryHistory.lastYearsPriceYieldLimits.lower.price / 100, "percent")} - ${amountFormatter(complementaryHistory.lastYearsPriceYieldLimits.higher.price / 100, "percent")}` },
                    { label: "Absolute price range", value: `${amountFormatter(complementaryHistory.absolutePriceYieldLimits.lower.price / 100, "percent")} - ${amountFormatter(complementaryHistory.absolutePriceYieldLimits.higher.price / 100, "percent")}` },
                    { label: "Last 52 week yield range", value: `${amountFormatter(complementaryHistory.lastYearsPriceYieldLimits.higher.yield / 100, "percent")} - ${amountFormatter(complementaryHistory.lastYearsPriceYieldLimits.lower.yield / 100, "percent")}` },
                    { label: "Absolute yield range", value: `${amountFormatter(complementaryHistory.absolutePriceYieldLimits.higher.yield / 100, "percent")} - ${amountFormatter(complementaryHistory.absolutePriceYieldLimits.lower.yield / 100, "percent")}` },
                ])
            } else {
                setComplementaryHistoryDisplay([
                    { label: "Day trading volume level", value: capitalizeEachFirstLetterFormatter(complementaryHistory.current_activity_level) },
                    { label: "Current trading volume level", value: capitalizeEachFirstLetterFormatter(complementaryHistory.current_activity_level) },
                    { label: "Last 52 week price range", value: "No available data for this time range." },
                    { label: "Absolute price range", value: `${amountFormatter(complementaryHistory.absolutePriceYieldLimits.lower.price / 100, "percent")} - ${amountFormatter(complementaryHistory.absolutePriceYieldLimits.higher.price / 100, "percent")}` },
                    { label: "Last 52 week yield range", value: "No available data for this time range." },
                    { label: "Absolute yield range", value: `${amountFormatter(complementaryHistory.absolutePriceYieldLimits.higher.yield / 100, "percent")} - ${amountFormatter(complementaryHistory.absolutePriceYieldLimits.lower.yield / 100, "percent")}` },
                ])
            }
        }

        if (bondDetail && complementaryHistory && plottingHistoricPoints.length > 0) {
            setIsBondDataLoading(false)
        }
    }, [bondDetail, complementaryHistory, plottingHistoricPoints])

    return (
        <LoadingWrapper isLoading={isBondDataLoading}>
            <Stack>
                <CompoundHeader bondDetail={bondDetail} copyIsinHandler={handleCopyIsin} />
                <Stack gap={2} sx={{ mt: 8 }}>
                    <HighlightCards highlights={bondHighlights} />
                    <SenseTechnicalAnalysisCard technicalAnalysis={senseTechnicalAnalysis} isTechnicalAnalysisLoading={isSenseTechnicalAnalysisLoading} handleGenerateAnalysis={handleGenerateTechnicalAnalysis} thoughtTime={senseTechnicalAnalysisThoughtTime} />
                    <ResponsiveLayout>
                        <Stack flex={{ xs: 1, lg: 0.75 }} gap={{ xs: 1, sm: 2 }} component={Paper} sx={{ p: 2 }}>
                            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent={{ xs: "flex-start", sm: "space-between" }}>
                                <Typography variant="subtitle1" fontWeight="bold">Trading chart</Typography>
                                <Button variant="textPrimary" onClick={handleOpenBondAnalyzer} endIcon={<KeyboardArrowRightIcon />}>Open on OmniChart</Button>
                            </Stack>
                            {bondDetail && <HistoricChart
                                plottingHistoricPoints={plottingHistoricPoints}
                                instrumentId={bondDetail.isin}
                                instrumentName={bondNameFormatter(bondDetail)}
                                plottingRange={plottingRange}
                                handlePlottingRangeChangeHandler={handlePlottingRangeChangeHandler}
                                chartRangeToggleOptions={chartRangeToggleOptions}
                                plottingType={plottingType}
                                handlePlottingTypeChangeHandler={handlePlottingTypeChangeHandler}
                                chartTypeToggleOptions={chartTypeToggleOptions}
                            />}
                        </Stack>
                        <DataCard flex={{ xs: 1, lg: 0.25 }} title="Master information" data={masterInformation} />
                    </ResponsiveLayout>
                    <ResponsiveLayout>
                        <DataCard flex={{ xs: 1, lg: 0.5 }} title="General information" data={generalInformation} />
                        <DataCard flex={{ xs: 1, lg: 0.5 }} title="Performance information" data={complementaryHistoryDisplay} />
                    </ResponsiveLayout>
                    {creditRatingInformation.length > 0 && <DataCard title="Credit rating information" data={creditRatingInformation} />}
                    {relatedBonds.length > 0 && <SimpleInstrumentGroup title="Related bonds" bonds={relatedBonds} />}
                </Stack>
            </Stack>
        </LoadingWrapper>
    );
};

export default Bond;